import { useMemo, useState, useCallback, useEffect } from 'react'
import { isEmpty, isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	TextField,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { makeStyles } from '@mui/styles'

import DoFormsFormTile from '../../../doforms-form/DoformsFormTile'

import { getDefaultOperator } from 'utils/functions/helpers'
import { LABEL_FOR_TYPE } from '../constant'

const useStyles = makeStyles(() => ({
	jobSummaryContainer: {
		display: 'flex',
		margin: '0 10px',
		width: '99%',
		height: '100%',
	},
	sideBarContainer: {
		display: 'flex',
		height: '100%',
		minWidth: '230px',
		justifyContent: 'center',
		alignItems: 'center',
		margin: '0 10px',
	},
	summaryContainer: {
		display: 'grid',
	},
	boxContain: {
		width: '100%',
		height: '100%',
		overflowY: 'auto',
	},
	contentContainer: {
		height: '100%',
		width: '100%',
		overflowY: 'hidden',
		overflowX: 'auto',
	},
	topNavContainer: {
		alignItems: 'center',
	},
	dialogPaper: {
		height: '90% !important',
	},
}))

function SummaryHeaderComponent(props) {
	const {
		tile,
		allColumns,
		filterField,
		setFilterText,
		setSearchText,
		startInterval,
		headerType,
		selectedSummaryForm,
		selectedCustomerForm,
		onMapButtonClick,
		onFilterButtionClick,
	} = props
	const classes = useStyles()
	const { t } = useTranslation('common')
	const { environment } = useSelector((state) => state)

	const [currentText, setCurrentText] = useState('')
	const selectedColumnField = useMemo(
		() => allColumns?.find((column) => column.name === filterField),
		[allColumns, filterField]
	)

	const [openDialog, setOpenDialog] = useState(false)
	const [titleLabel, setTitleLabel] = useState('')
	const [displayProjectFormInfo, setDisplayProjectFormInfo] = useState(null)

	function buildTitleLabel(projectFormInfo) {
		if (!projectFormInfo) return ''
		return `${projectFormInfo.projectName} / ${projectFormInfo.formName}`
	}

	function handleOpenDialog(projectFormInfo) {
		setOpenDialog(true)
		setTitleLabel(buildTitleLabel(projectFormInfo))
		setDisplayProjectFormInfo(projectFormInfo)
	}

	function handleCloseDialog(shouldUseInterval = false) {
		setOpenDialog(false)
		setDisplayProjectFormInfo(null)
		if (startInterval && shouldUseInterval) {
			startInterval()
		}
	}

	function doSetFilter(column, searchText) {
		if (isEmpty(searchText)) {
			setFilterText(null)
			return
		}

		if (!column) return

		const defaultOperator = getDefaultOperator(column.type)
		setFilterText({
			field: column.name,
			operator: defaultOperator,
			value: searchText,
			type: column.type,
			id: 'searchTextFilter',
		})
	}

	const displayDialog = useMemo(
		() => displayProjectFormInfo?.projectKey && displayProjectFormInfo?.formKey,
		[displayProjectFormInfo]
	)

	return (
		<div>
			<Grid
				container
				spacing={1}
				className={classes.topNavContainer}
				alignItems="center"
				paddingLeft="8px"
				paddingBottom="5px"
				paddingTop="5px"
			>
				<Grid item xs md lg>
					<Grid container spacing={1} alignItems="center">
						<Grid item xs md lg>
							<TextField
								id="search-input"
								value={currentText}
								onChange={(e) => {
									const v = e.target.value
									setCurrentText(v)
								}}
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										setSearchText(currentText)
										//doSetFilter(selectedColumnField, currentText)
									}
								}}
								InputLabelProps={{ shrink: false }}
								placeholder={t('misc.search')}
								size="small"
								fullWidth
								sx={{ maxWidth: '100%' }}
							/>
						</Grid>
						<Grid item xs={2} md={2} lg={2}>
							<Button
								fullWidth
								variant="outlined"
								onClick={() => setSearchText(currentText)}
								startIcon={<SearchOutlinedIcon />}
								size="small"
								sx={{ textTransform: 'none', height: 40, minWidth: 60 }}
							>
								Search
							</Button>
						</Grid>

						<Grid item xs={2} md={2} lg={2}>
							<Button
								fullWidth
								variant="outlined"
								onClick={(event) => {
									if (!onFilterButtionClick) {
										doSetFilter(selectedColumnField, currentText)
										return
									}

									onFilterButtionClick(event)
								}}
								startIcon={<FilterAltOutlinedIcon />}
								size="small"
								sx={{ textTransform: 'none', height: 40, minWidth: 60 }}
							>
								{t('misc.filter')}
							</Button>
						</Grid>
					</Grid>
				</Grid>

				<Grid item xs={6} md={2} lg={2}>
					<Button
						variant="contained"
						size="small"
						onClick={() => handleOpenDialog(selectedSummaryForm)}
						sx={{ height: '40px', minWidth: '100px', textTransform: 'none', width: '100%' }}
					>
						+ {t(LABEL_FOR_TYPE[headerType])}
					</Button>
				</Grid>

				<Grid item xs={6} md={2} lg={2}>
					<Button
						variant="contained"
						size="small"
						onClick={() => handleOpenDialog(selectedCustomerForm)}
						sx={{ height: '40px', minWidth: '100px', textTransform: 'none', width: '100%' }}
					>
						+ {t('misc.customer')}
					</Button>
				</Grid>

				<Grid item xs={12} md={2} lg={2}>
					<Button
						variant="contained"
						size="small"
						sx={{
							height: '40px',
							minWidth: '100px',
							textTransform: 'none',
							backgroundColor: '#CC0000',
							'&:hover': { backgroundColor: '#b70e0e' },
							width: '100%',
						}}
						onClick={onMapButtonClick}
					>
						{t(LABEL_FOR_TYPE[headerType])} Map
					</Button>
				</Grid>
			</Grid>

			{displayDialog && (
				<Dialog
					fullWidth
					maxWidth="md"
					aria-labelledby="transition-modal-title"
					aria-describedby="transition-modal-description"
					open={openDialog}
					closeAfterTransition
					PaperProps={{ className: classes.dialogPaper }}
				>
					<DialogTitle
						id="record-dialog-title"
						sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
					>
						<div>{titleLabel}</div>
						<div>
							<IconButton onClick={() => handleCloseDialog()} aria-label="close">
								<CloseIcon />
							</IconButton>
						</div>
					</DialogTitle>

					<DialogContent sx={{ height: 'calc(90vh - 100px)' }}>
						{displayProjectFormInfo && (
							<DoFormsFormTile
								tile={{ ...tile, isShowDialog: true }}
								openTileFrmGrid={true}
								initProjectFormInfo={displayProjectFormInfo}
								setIsOpenDialog={handleCloseDialog}
								useSettings={false}
								handleChangeTitle={(projectFormInfo) => {
									const newTitleLabel = buildTitleLabel(projectFormInfo)
									if (isEqual(titleLabel, newTitleLabel)) return
									setTitleLabel(newTitleLabel)
								}}
							/>
						)}
					</DialogContent>
				</Dialog>
			)}
		</div>
	)
}

export default SummaryHeaderComponent
